// Modified version of https://github.com/apearce/use-immediate-effect
// In particular, I added types!

/*
MIT License

Copyright (c) 2021 apearce

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

import { useEffect, useRef, EffectCallback } from 'react';

import shallowArrayDiff from './shallowArrayDiff';

/*
 * The same API as useEffect, but calls the function inline instead of
 * asynchronously after the render.
 */
export default function useImmediateEffect(
  effect: EffectCallback,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deps: Array<any>
): void {
  const cleanupRef = useRef<(() => void) | void>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const depsRef = useRef<Array<any>>();

  if (!depsRef.current || shallowArrayDiff(depsRef.current, deps)) {
    depsRef.current = deps;

    if (cleanupRef.current) {
      cleanupRef.current();
    }

    cleanupRef.current = effect();
  }

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);
}
